// Host-side state + persistence for per-form column layout overrides (width +
// order). A host component that owns the transaction model uses this hook to
// hand a `GridLayoutApi` down to its grids (list / multi-tabular / child); the
// grids keep edits as a live override and call `apply(formNo)` to bake them
// back into the design. `x` is regenerated as (visualIndex + 1) * 10 — the same
// convention the transaction designer's GridPreview reorder uses — so the grid
// order changes while the single-record form layout (ordered by `y`) is
// untouched. Updating the in-memory model via `setModel` keeps the view from
// snapping back when the override clears; callers holding a read-only model
// prop should pass a working copy + its setter.

import { useCallback, useMemo, useState } from 'react'

import { allSqlModels, layoutOrderedColumns, parseApiError } from '../utils'
import type { ColumnDef, TransactionModel } from '../types'
import type { OverlaySetLeaf } from './gridTypes'
import {
  colKeyOf, layoutIsDirty, orderedDisplayCols, widthOf,
  type GridLayout, type GridLayoutApi,
} from './columnLayout'

// Stable overlay identity for a column: prefer col_id (the field the engine's
// overlay merge matches first), then db_name, then name.
function overlayKeyOf(c: ColumnDef): { field: string; value: string } | null {
  if (c.col_id) return { field: 'col_id', value: String(c.col_id) }
  if (c.db_name) return { field: 'db_name', value: String(c.db_name) }
  if (c.name) return { field: 'name', value: String(c.name) }
  return null
}

/** User feedback sink — the hosting app injects its own toast/notification
 *  implementation (the package carries no toast-library dependency). */
export interface GridLayoutNotify {
  success(msg: string): void
  error(msg: string): void
}

/** Persistence backend — the hosting app injects how "Apply layout" writes:
 *  `saveDesign` bakes the mutated model into the shared transaction JSON;
 *  `saveOverlay` stores the width/placement leaves as a per-tenant model
 *  overlay. Only the branch matching `mode` is called. */
export interface GridLayoutPersistence {
  saveDesign: (project: string, objName: string, model: TransactionModel) => Promise<unknown>
  saveOverlay: (project: string, objName: string, set: OverlaySetLeaf[]) => Promise<unknown>
}

export function useGridLayout(opts: {
  model: TransactionModel | null
  setModel: (m: TransactionModel) => void
  project: string
  objName: string
  canEdit: boolean
  /** Where "Apply layout" persists: 'design' bakes width/order into the shared
   *  transaction JSON (designer live preview); 'customize' stores them as a
   *  per-tenant model overlay (run app / downloaded app) so the core design is
   *  untouched. */
  mode?: 'design' | 'customize'
  notify: GridLayoutNotify
  persistence: GridLayoutPersistence
}): GridLayoutApi {
  const { model, setModel, project, objName, canEdit, mode = 'design', notify, persistence } = opts
  const customize = mode === 'customize'
  const [layouts, setLayouts] = useState<Record<string, GridLayout>>({})
  const [applyingFormNo, setApplyingFormNo] = useState<string | null>(null)

  const setWidth = useCallback((formNo: string, colKey: string, width: number) => {
    setLayouts((prev) => {
      const cur = prev[formNo] || { widths: {}, order: null }
      return { ...prev, [formNo]: { ...cur, widths: { ...cur.widths, [colKey]: width } } }
    })
  }, [])
  const setOrder = useCallback((formNo: string, keys: string[]) => {
    setLayouts((prev) => {
      const cur = prev[formNo] || { widths: {}, order: null }
      return { ...prev, [formNo]: { ...cur, order: keys } }
    })
  }, [])
  const reset = useCallback((formNo: string) => {
    setLayouts((prev) => {
      if (!prev[formNo]) return prev
      const next = { ...prev }
      delete next[formNo]
      return next
    })
  }, [])

  const apply = useCallback((formNo: string) => {
    void (async () => {
      if (!project || !objName || !model) return
      const layout = layouts[formNo]
      if (!layoutIsDirty(layout)) return
      setApplyingFormNo(formNo)
      try {
        const clone: TransactionModel = JSON.parse(JSON.stringify(model))
        const target = allSqlModels(clone).find((x) => x.formNo === formNo)?.sm
        if (!target) { notify.error('Could not locate the form in the design'); return }
        const display = orderedDisplayCols(layoutOrderedColumns(target), layout)
        const overlaySet: OverlaySetLeaf[] = []
        display.forEach((c, i) => {
          const wrap = target.columns.find((w) => colKeyOf(w.column) === colKeyOf(c))
          if (!wrap) return
          const x = String((i + 1) * 10)
          const width = widthOf(c, layout)
          wrap.column.x = x
          wrap.column.width = width
          if (customize) {
            const k = overlayKeyOf(wrap.column)
            if (!k) return
            const base = `transaction.sql_models.@items.${formNo}.columns.@items.${k.value}.`
            overlaySet.push({ path: `${base}x`, value: x, key: k.field })
            overlaySet.push({ path: `${base}width`, value: width, key: k.field })
          }
        })
        if (customize) {
          // Store width + placement as a per-tenant overlay; the shared design
          // JSON is never touched. The in-memory mutation above keeps the grid
          // in sync immediately (overlays re-merge on the next load).
          await persistence.saveOverlay(project, objName, overlaySet)
          notify.success('Column layout saved to your customization')
        } else {
          await persistence.saveDesign(project, objName, clone)
          notify.success('Column layout saved to the transaction design')
        }
        setModel(clone)
        reset(formNo)
      } catch (err) {
        const { message } = parseApiError(err)
        notify.error(`Could not apply layout: ${message}`)
      } finally {
        setApplyingFormNo(null)
      }
    })()
  }, [project, objName, model, layouts, reset, setModel, customize, notify, persistence])

  const applyLabel = customize ? 'Save to customization' : 'Apply layout'
  const applyTitle = customize
    ? "Save these column widths & order to your organization's customization (the original design is unchanged)"
    : 'Save the new column widths & order into the transaction design'

  return useMemo<GridLayoutApi>(() => ({
    layouts, setWidth, setOrder, reset, apply, canEdit, applyingFormNo,
    applyLabel, applyTitle,
  }), [layouts, setWidth, setOrder, reset, apply, canEdit, applyingFormNo,
    applyLabel, applyTitle])
}
